<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import { isDateValid } from "~/utils/is-date-valid"

const operationStore = useOperationStore()
const { t } = useI18n()

const REMARK_MAX_LENGTH = 1000

const withdrawnOptions = [
  {
    label: "Rücknahme durch Antragstellende (Grund nicht bekannt)",
    value: "applicantWithdrawal",
  },
  {
    label: "Rücknahme - sachliche Unzuständigkeit ",
    value: "notLocallyCompetent",
  },
  {
    label: "Rücknahme - fehlender NRW-Bezug",
    value: "notSubjectCompetent",
  },
]

const federalStateOptions = [
  { label: "Baden-Württemberg", value: "BW" },
  { label: "Bayern", value: "BY" },
  { label: "Berlin", value: "BE" },
  { label: "Brandenburg", value: "BB" },
  { label: "Bremen", value: "HB" },
  { label: "Hamburg", value: "HH" },
  { label: "Hessen", value: "HE" },
  { label: "Mecklenburg-Vorpommern", value: "MV" },
  { label: "Niedersachsen", value: "NI" },
  { label: "Nordrhein-Westfalen", value: "NW" },
  { label: "Rheinland-Pfalz", value: "RP" },
  { label: "Saarland", value: "SL" },
  { label: "Sachsen", value: "SN" },
  { label: "Sachsen-Anhalt", value: "ST" },
  { label: "Schleswig-Holstein", value: "SH" },
  { label: "Thüringen", value: "TH" },
]

const schema = yup.object().shape({
  result: yup.string().required("Pflichtfeld"),

  withdrawalReason: yup.string().when("result", {
    is: "withdrawn",
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  resultDate: yup
    .string()
    .required("Pflichtfeld")
    .test("is-date-valid", "Ungültiges Datum", (value?: string) => {
      return value ? isDateValid(value) : true
    }),

  remark: yup
    .string()
    .max(
      REMARK_MAX_LENGTH,
      `Maximale Anzahl von ${REMARK_MAX_LENGTH} Zeichen erreicht`
    ),

  federalState: yup.string(),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

watch(
  () => state.result,
  (value) => {
    if (value !== "withdrawn") {
      state.withdrawalReason = undefined
    }
  }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Antragsabschluss"
      />
    </template>

    <IffContentSection headline="Antragsabschluss">
      <UFormGroup name="result" label="Grund des Antragsabschlusses" required>
        <div class="flex flex-col gap-2">
          <URadio
            value="withdrawn"
            v-model="state.result"
            label="Antragsrücknahme"
            aria-controls="withdrawal-reason-region"
          />
          <div class="flex items-center gap-2">
            <URadio
              value="stateChange"
              v-model="state.result"
              label="Beendigung - Bundeslandwechsel"
              aria-controls="withdrawal-reason-region"
            />
            <IffIconTooltip
              text="Künftiges Bundesland der antragstellenden Person."
            />
          </div>
        </div>
      </UFormGroup>
      <div id="withdrawal-reason-region" aria-live="polite">
        <IffTransitionHeight>
          <div v-if="state.result === 'withdrawn'">
            <UFormGroup
              name="withdrawalReason"
              label="Grund der Antragsrücknahme"
              required
            >
              <URadioGroup
                v-model="state.withdrawalReason"
                :options="withdrawnOptions"
              />
            </UFormGroup>
          </div>
          <div v-if="state.result === 'stateChange'">
            <UFormGroup name="federalState" label="Bundesland" class="max-w-md">
              <template #hint>
                <IffIconTooltip
                  text="Künftiges Bundesland der antragstellenden Person."
                />
              </template>
              <USelectMenu
                v-model="state.federalState"
                :options="federalStateOptions"
                value-attribute="value"
                placeholder="Bitte wählen"
                searchable
                :searchable-placeholder="
                  t('iff.common.select-search-placeholder')
                "
              />
            </UFormGroup>
          </div>
        </IffTransitionHeight>
      </div>

      <UFormGroup
        name="resultDate"
        label="Datum Antragsabschluss"
        required
        class="max-w-[210px]"
      >
        <template #hint>
          <UButton
            @click="state.resultDate = new Date().toISOString().slice(0, 10)"
            icon="i-ic-outline-access-time"
            size="sm"
            color="gray"
            variant="ghost"
            :trailing="false"
            aria-label="Aktuelles Datum wählen"
            title="Aktuelles Datum wählen"
          />
        </template>

        <UInput
          v-model="state.resultDate"
          type="date"
          aria-required="true"
          class="whitespace-normal max-w-[150px]"
        />
      </UFormGroup>
    </IffContentSection>

    <IffContentSection headline="Weitere Angaben" :is-last-child="true">
      <UFormGroup
        name="remark"
        label="Bemerkung"
        :help="`Noch ${
          REMARK_MAX_LENGTH - (state.remark?.length ?? 0)
        } Zeichen verfügbar`"
      >
        <UTextarea
          v-model="state.remark"
          :maxlength="REMARK_MAX_LENGTH"
          resize
        />
      </UFormGroup>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
